import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Spinner from 'react-bootstrap/Spinner'
import Page from '../../../component/page'
import ApplyReport from './ApplyReport'
import CountrySummary from './CountrySummary'
import HidesPass from './HidesPass'
import MergesPass from './MergesPass'
import LayoutMergesPass from './LayoutMergesPass'
import LayoutsPass from './LayoutsPass'
import NamesPass from './NamesPass'
import PhantomsPass from './PhantomsPass'
import PrivatePass from './PrivatePass'
import { isAdmin } from '../isAdmin'
import { nameById as countryNameById } from '../../../js/countries'

// Course curation.
//
// The passes run in a fixed order — names, then trash, then merges — because
// merge clustering compares names and hiding removes the trash that would
// otherwise sit inside a merge cluster. The tabs are numbered to say so.
//
// The ordering is advisory, not enforced. The server is stateless and has no
// notion of a country being "done", and somebody who knows the names are already
// clean should not be made to recompute them to get to the merges. The rule lives
// in docs/course-curation.md; this only makes it hard to miss.
//
// `outstanding` says how many rows on that tab are still waiting for a decision, and
// returns null for "not analysed yet" rather than zero — a country whose passes have
// never run has nothing to say about which tab to open, and must not be shown as clean.
//
// It reads the loaded pass first and the country summary second. The summary is filled
// in as a by-product of a pass being computed, so it is the only thing available before
// a tab is opened — but it is a snapshot, and the pass that is actually on screen is
// newer than it. The layouts pass has no summary column at all, so its tab stays
// unknown until it has been opened once.

// Null unless at least one of the counts is a real number, and then the sum of the ones
// that are.
const sumOrNull = (values) =>
  values.some((v) => v != null)
    ? values.reduce((total, v) => total + (v ?? 0), 0)
    : null

const PASSES = [
  {
    key: 'private',
    label: '0. Private',
    // Before names: every course it unpublishes leaves all the other passes, which only
    // see courses someone other than the creator can use. The summary count is always
    // known, since it is one grouped query rather than a pass.
    outstanding: (data, row) => (data ? data.candidates.length : row?.privateCandidates ?? null),
  },
  {
    key: 'names',
    label: '1. Names',
    outstanding: (data, row) =>
      data
        ? data.proposals.length
        : sumOrNull([row?.auto, row?.review, row?.needsLookup]),
  },
  {
    key: 'hides',
    label: '2. Trash',
    outstanding: (data, row) => (data ? data.candidates.length : row?.hideCandidates ?? null),
  },
  {
    key: 'merges',
    label: '3. Merges',
    outstanding: (data, row) => (data ? data.groups.length : row?.mergeGroups ?? null),
  },
  {
    key: 'layouts',
    label: '4. Layouts',
    // Everything the tab can act on. `mismatched` is reported and never renamed here,
    // and a course-name candidate is decided on the names tab, so neither counts.
    outstanding: (data) => (data ? data.autoApply.length + data.review.length : null),
  },
  {
    key: 'layoutmerges',
    label: '5. Layout merges',
    // After the layout renames: a rename can turn a reviewed pair into an identical one. No
    // summary column, so like the layouts tab it is unknown until opened.
    outstanding: (data) => (data ? data.auto.length + data.manual.length : null),
  },
  {
    key: 'phantoms',
    label: '6. Phantoms',
    // Candidates nobody has searched for yet. The summary only knows the total, which is
    // the same number until the first review is recorded.
    outstanding: (data, row) =>
      data ? data.candidates.filter((c) => !c.review).length : row?.phantomCandidates ?? null,
  },
]

// The phantoms tab records verdicts through its own endpoint, but hides through the
// hides one: hiding a phantom is exactly a hide, with the same staleness check and audit.
const applyPassFor = (pass) => (pass === 'phantoms' ? 'hides' : pass)

const reviewPhantomsAction = (decisions) => ({
  type: 'CURATION_REVIEW_PHANTOMS',
  payload: {
    request: { url: '/admin/curation/phantoms/review', method: 'post', data: { decisions } },
  },
})

const loadCountries = () => ({
  type: 'CURATION_LOAD_COUNTRIES',
  payload: { request: { url: '/admin/curation/countries' } },
})

// The store and action types key a pass by one word, and CURATION_LOAD_X is split on '_'; the
// API path is hyphenated.
const pathOf = (pass) => (pass === 'layoutmerges' ? 'layout-merges' : pass)

const loadAction = (pass, country, refresh) => ({
  type: `CURATION_LOAD_${pass.toUpperCase()}`,
  payload: {
    request: {
      url: `/admin/curation/${pathOf(pass)}?country=${country}${refresh ? '&refresh=true' : ''}`,
    },
  },
})

const applyAction = (pass, body) => ({
  type: `CURATION_APPLY_${pass.toUpperCase()}`,
  payload: {
    request: {
      url: `/admin/curation/${pathOf(pass)}/apply`,
      method: 'post',
      data: body,
    },
  },
})

// The layouts tab is the one pass that writes through two endpoints: its own, and the
// course-name one, because a layout that holds a fuller version of its course's name is
// a course rename that you can only see from here. Two requests mean two reports, and
// the store holds one — so they are added together and shown as the single batch the
// page actually submitted.
const mergeReports = (first, second) => {
  if (!first) return second
  if (!second) return first
  if (first.failed) return first
  if (second.failed) return second
  return {
    dryRun: first.dryRun,
    applied: first.applied + second.applied,
    skipped: first.skipped + second.skipped,
    rejected: first.rejected + second.rejected,
    outcomes: [...first.outcomes, ...second.outcomes],
  }
}

// The response body is text — the axios client sets responseType: 'text', which is why
// the reducer parses it too.
const reportOf = (result) => {
  try {
    return JSON.parse(result.payload.data)
  } catch (e) {
    return null
  }
}

const ago = (timestamp) => {
  if (!timestamp) return null
  const minutes = Math.floor((Date.now() - timestamp) / 60000)
  if (minutes < 1) return 'just now'
  return `${minutes} min ago`
}

export default function Curation() {
  const dispatch = useDispatch()
  const user = useSelector((state) => state.auth.user)
  const curation = useSelector((state) => state.curation)
  const [pass, setPass] = useState('names')
  // The passes only appear once a country is picked, below the country table, and on a
  // long table that is off screen. Scroll them into view rather than leaving the click
  // looking like it did nothing.
  const passesRef = useRef(null)
  // Apply stays disabled until a dry run for this pass has been read. Reset on
  // every switch, so a dry run of the names queue cannot unlock a merge.
  const [dryRunSeen, setDryRunSeen] = useState(false)

  const { country, report, applying } = curation
  const passState = curation[pass]

  useEffect(() => {
    dispatch(loadCountries())
  }, [dispatch])

  useEffect(() => {
    if (country) dispatch(loadAction(pass, country))
  }, [dispatch, pass, country])

  useEffect(() => {
    setDryRunSeen(false)
  }, [pass, country])

  useEffect(() => {
    if (country) passesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [country])

  if (!isAdmin(user)) return null

  // Recomputing this pass and then re-reading the country table, in that order.
  //
  // The summary's bucket counts are not stored anywhere: the server fills them in from
  // its own cache of computed proposals, and every apply drops that cache for the whole
  // country. So a write leaves the row on screen showing the numbers from before it,
  // which is the one place this page could actively mislead — you cannot tell a country
  // you have just finished from one you have not started. Reading the countries only
  // after the recompute has landed is what makes the row show the post-write numbers
  // rather than the dashes the cleared cache would otherwise report.
  const recompute = () =>
    dispatch(loadAction(pass, country, true))
      .then(() => dispatch(loadCountries()))
      .catch(() => {})

  const bodyFor = (targetPass, dryRun, decisions) =>
    targetPass === 'merges' || targetPass === 'layoutmerges'
      ? { dryRun, groups: decisions }
      : { dryRun, decisions }

  // What a pass hands back: the layouts tab submits an object with two lists, every
  // other pass a single array. Normalised here rather than in four components.
  const partsOf = (decisions) =>
    Array.isArray(decisions)
      ? [{ pass: applyPassFor(pass), decisions }]
      : [
          { pass: 'layouts', decisions: decisions.layouts || [] },
          { pass: 'names', decisions: decisions.courseNames || [] },
        ].filter((part) => part.decisions.length > 0)

  // `options.silent` is for a submit that speaks for part of the page rather than all of it --
  // the merges tab applying a single group. Such a dry run must not set `dryRunSeen`, or reading
  // the preview of one group would unlock the apply button for every other group on the page.
  const submit = (dryRun) => (decisions, onDone, options) => {
    const parts = partsOf(decisions)
    if (parts.length === 0) return

    // redux-axios-middleware hands back the request promise, and rejects it on a
    // failed request. The _FAIL case has already put the error in the store by
    // then, so the catch only has to stop it surfacing as an unhandled rejection —
    // and must not run the success path.
    //
    // The parts run in series, not in parallel: each one's _SUCCESS overwrites the
    // report in the store, and a failure must stop the ones after it rather than
    // half-apply a batch somebody read one dry run for.
    parts
      .reduce(
        (chain, part) =>
          chain.then((soFar) =>
            dispatch(applyAction(part.pass, bodyFor(part.pass, dryRun, part.decisions)))
              .then((result) => mergeReports(soFar, reportOf(result)))
          ),
        Promise.resolve(null)
      )
      .then((merged) => {
        // Only the last part's report reached the store, so put the sum back.
        if (merged && parts.length > 1) {
          dispatch({ type: 'CURATION_SET_REPORT', payload: merged })
        }
        if (dryRun) {
          if (!options?.silent) setDryRunSeen(true)
          return
        }
        // The rows just applied are still on screen, and re-applying them would
        // produce a wall of SKIPPED_STALE that reads like a bug rather than like
        // convergence. Clear the saved draft and refetch instead.
        if (onDone) onDone()
        if (!options?.silent) setDryRunSeen(false)
        recompute()
      })
      .catch(() => {})
  }

  // Undo for the batch just applied, built from the before/after the report carries.
  // It goes through the same apply endpoint, so it is protected by the same staleness
  // check: anything changed since is skipped rather than clobbered.
  //
  // This is lost on a page reload -- there is no decisions table, so the report held in
  // memory is the only record of what the batch changed.
  const revert = () => {
    const decisions = (report?.outcomes || [])
      .filter((o) => o.status === 'APPLIED' && o.afterName && o.beforeName)
      .map((o) => ({
        courseId: o.entityId,
        oldName: o.afterName,
        newName: o.beforeName,
        ...(o.beforeSecondaryName === null || o.beforeSecondaryName === undefined
          ? {}
          : { newSecondaryName: o.beforeSecondaryName }),
      }))
    if (decisions.length === 0) return
    submit(false)(decisions, () => dispatch(loadAction(pass, country, true)))
  }

  const countryRow = (curation.countries.data || []).find((row) => row.country === country)

  // Only the pass on screen has been loaded, and the store still holds the previous
  // country's while the new one is in flight — so a pass is only allowed to speak for
  // the country it was computed for.
  const loadedData = (key) => {
    const data = curation[key]?.data
    return data && data.country === country ? data : null
  }

  const outstandingFor = (p) => p.outstanding(loadedData(p.key), countryRow)

  // Picking a country opens the first tab that has something waiting, so the work starts
  // where it is rather than always on names. Only the summary counts are known at this
  // point; nothing outstanding, or nothing analysed yet, falls back to the first pass —
  // which is where the fixed order says to start anyway.
  const selectCountry = (code) => {
    dispatch({ type: 'CURATION_SET_COUNTRY', payload: code })
    const row = (curation.countries.data || []).find((r) => r.country === code)
    const first = PASSES.find((p) => p.outstanding(null, row) > 0)
    setPass(first ? first.key : PASSES[0].key)
  }

  // The pass on screen, but only if it is this country's. A pass stays mounted through a
  // recompute now, so the store can hold the previous country's rows while the new one is
  // in flight — and rendering those under the new country's heading is worse than a spinner.
  const loaded = loadedData(pass)

  const passProps = {
    country,
    data: loaded,
    applying,
    dryRunSeen,
    onDryRun: submit(true),
    onApply: submit(false),
  }

  return (
    <Page className="container admin curation" hideAds>
      <h1>Course curation</h1>

      <CountrySummary
        countries={curation.countries.data}
        loading={curation.countries.loading}
        selected={country}
        onSelect={selectCountry}
      />

      {country && (
        <div ref={passesRef}>
          {/* Picking a country scrolls the table out of view, and with it the only thing that
              said which country this is. */}
          <h2 className="mt-4 mb-0">
            {countryRow?.countryName || countryNameById(country)}
            {countryRow?.countryCode && (
              <span className="text-muted fs-5 ms-2">({countryRow.countryCode})</span>
            )}
          </h2>
          <Nav variant="tabs" activeKey={pass} onSelect={setPass} className="mt-3">
            {PASSES.map((p) => {
              const count = outstandingFor(p)
              return (
                <Nav.Item key={p.key}>
                  <Nav.Link
                    eventKey={p.key}
                    className={
                      count > 0 ? 'curation-tab-pending' : count === 0 ? 'text-muted' : undefined
                    }
                  >
                    {p.label}
                    {count > 0 && (
                      <Badge bg="warning" text="dark" className="ms-2">
                        {count}
                      </Badge>
                    )}
                  </Nav.Link>
                </Nav.Item>
              )
            })}
          </Nav>

          <div className="d-flex justify-content-between align-items-center my-2">
            <span className="text-muted small">
              {passState?.data
                ? `computed ${ago(passState.data.generatedAt)}`
                : null}
            </span>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={passState?.loading}
              onClick={recompute}
            >
              Recompute
            </Button>
          </div>

          <ApplyReport
            report={report}
            onDismiss={() => dispatch({ type: 'CURATION_CLEAR_REPORT' })}
            // Names only. A rename is undone by another rename, so the inverse is a
            // real decision the same guard protects. A merge has no inverse -- it
            // re-points layouts and writes version-chain links -- and a hide is
            // undone from the course manager, so neither offers one here.
            onRevert={pass === 'names' ? revert : undefined}
          />

          {passState?.loading && (
            <p>
              <Spinner size="sm" className="me-2" />
              Working through every active course in this country…
            </p>
          )}

          {passState?.error && (
            <p className="text-danger">{String(passState.error)}</p>
          )}

          {/* Kept mounted while a recompute is in flight, dimmed rather than replaced.
              Applying one group refetches the whole pass, and swapping a page of cards
              for a one-line spinner collapses the document — the browser then clamps
              the scroll position to the new height, which is the top. You lose your
              place, every open survivor editor, every per-group preview and the
              dropped-members accordion, all for a list the store still holds. The
              reducer keeps `data` through a load precisely so this is possible. */}
          <div className={passState?.loading ? 'curation-refreshing' : undefined}>
            {loaded && pass === 'private' && <PrivatePass {...passProps} />}
            {loaded && pass === 'names' && <NamesPass {...passProps} />}
            {loaded && pass === 'hides' && <HidesPass {...passProps} />}
            {loaded && pass === 'merges' && (
              <MergesPass
                {...passProps}
                namesOutstanding={curation.names.data?.counts?.review ?? 0}
              />
            )}
            {loaded && pass === 'layouts' && <LayoutsPass {...passProps} />}
            {loaded && pass === 'layoutmerges' && <LayoutMergesPass {...passProps} />}
            {loaded && pass === 'phantoms' && (
              <PhantomsPass
                {...passProps}
                countryName={countryRow?.countryName || countryNameById(country)}
                onReview={(decisions) => dispatch(reviewPhantomsAction(decisions)).then(reportOf)}
              />
            )}
          </div>
        </div>
      )}

      {!country && !curation.countries.loading && (
        <Form.Text>Pick a country to start. Sweden and Finland hold most of the data.</Form.Text>
      )}
    </Page>
  )
}
